import { useEffect, useRef } from "react";
import { Search, X } from "lucide-react";
import SearchResults, { type NootCompoundSearchItem } from "@/components/SearchResults";
import type { CompoundSearchItem, DbListing } from "@/hooks/useDatabase";

/**
 * Full-screen search for phones. The homepage hero opens it from its search box, and the navbar opens it below
 * 640px, where its dropdown panel does not fit (the panel started 28px off the left edge on 360 to 390px phones).
 * One sheet, so both searches look and behave the same: the input on top, the results under it, a 44px close
 * button, Escape closes it, and the page behind it does not scroll.
 *
 * Render it outside any element with a transform or a backdrop filter (the navbar has one), or `fixed` would be
 * measured from that element instead of the screen.
 */
export default function SearchSheet({
  query,
  onQueryChange,
  onClose,
  onEnter,
  compounds,
  vendors,
  listings,
  nootropics,
}: {
  query: string;
  onQueryChange: (q: string) => void;
  onClose: () => void;
  /** Enter in the input. The caller opens its top match. */
  onEnter: () => void;
  compounds: CompoundSearchItem[];
  vendors: { id: string; name: string; slug: string; logo: string | null; priority?: number }[];
  listings?: DbListing[];
  nootropics?: NootCompoundSearchItem[];
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <div role="dialog" aria-modal="true" aria-label="Search" className="fixed inset-0 z-modal flex flex-col bg-background animate-in fade-in-0 duration-150">
      <div className="flex items-center gap-3 border-b border-border p-4">
        <Search className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          aria-label="Search compounds, vendors and tools"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onEnter();
          }}
          placeholder="Search compounds, vendors and tools"
          className="flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground"
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close search"
          className="flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <SearchResults query={query} compounds={compounds} vendors={vendors} listings={listings} nootropics={nootropics} onClose={onClose} mobile />
      </div>
    </div>
  );
}
